import {
  setJob,
  setJobs,
  setLoadingJob,
  setLoadingJobs,
} from '../../store/jobs/jobsSlice';
import apiService from '@sonifi/wb-core/services/api.service';
import {
  setExpandedRow,
  setTablePagination,
} from '@sonifi/wb-widget/store/tableConfig/tableConfigSlice';
import { TABLE_KEYS } from '@sonifi/wb-widget/store/tableConfig/tableConfigConstants';
import { LOADING_STATUS } from '@sonifi/wb-core/common/Constants';
import { setAlert } from '@sonifi/wb-notifier/store/notifierSlice';
import { setFormErrors } from '@sonifi/wb-widget/store/formErrors/formErrorsSlice';
import { FORM_ERRORS_IDS } from '@sonifi/wb-widget/store/formErrors/formErrorsConstants';
import { ENDPOINTS } from '@sonifi/wb-core/services/endpointsConstants';
import {
  getIdFromUrl,
  parseQueryParams,
} from '@sonifi/wb-core/utils/parseHelper';
import { STATUS, isJobCancelable } from '../../../wb-core/services/jobsHelper';
import { GROUPS_KEYS } from '@sonifi/wb-group/store/groupsConstants';
import { JOB_TYPES } from '@sonifi/wb-admin/constants/wbAdminConstants';
import { defaultConfig } from '../../../../config-env';

export const getJobs =
  ({ sortBy = 'id', sortDir = 'desc', page = 1, pageSize = 100, query } = {}) =>
  async (dispatch) => {
    const params = {
      _order_by: `${sortDir === 'desc' ? '-' : ''}${sortBy}`,
      page,
      _query: query,
      rpp: pageSize,
    };
    try {
      dispatch(setLoadingJobs(LOADING_STATUS.loading));
      const url = `${ENDPOINTS.JOBS}?${parseQueryParams(params)}`;
      const res = await apiService.get(url);
      const jobs = res.data?.objects || [];

      dispatch(
        setTablePagination({
          query,
          page,
          pageSize,
          sortBy,
          sortDir,
          total: res.data.total,
          tableKey: TABLE_KEYS.adminJobs,
        }),
      );

      const jobsMapped = jobs.map((j) => ({
        ...j,
        steps: `${j.steps_complete || ''}/${j.steps_total || ''}`,
      }));

      await Promise.all([
        dispatch(setJobs({ jobs: jobsMapped })),
        dispatch(setLoadingJobs(LOADING_STATUS.success)),
      ]);
    } catch (error) {
      dispatch(setLoadingJobs(LOADING_STATUS.error));
    }
  };

const getGroupFromJob = ({ groups, job, targetDetail }) => {
  if (
    job.type === JOB_TYPES.PUBLISH_SINGLE_CHANNEL ||
    job.type === JOB_TYPES.PUBLISH_IRD_MENU ||
    job.type === JOB_TYPES.PUBLISH_SERVICE_MENU
  ) {
    const targetWithGroup = targetDetail.find((t) =>
      Object.getOwnPropertyDescriptor(t, 'group'),
    );
    const group = groups[GROUPS_KEYS.allGroups].find((g) =>
      targetWithGroup.group.startsWith(g.uri),
    );
    return group;
  }
  return undefined;
};

const getJobDependence = async (url) => {
  try {
    const res = await apiService.get(url);
    return { ...res.data };
  } catch (error) {
    return null;
  }
};

export const getJobById = (id) => async (dispatch, getState) => {
  try {
    dispatch(setLoadingJob(LOADING_STATUS.loading));
    dispatch(setJob({ job: null }));
    const url = `${ENDPOINTS.JOBS}/${id}`;
    const res = await apiService.get(url).catch((_) => ({
      data: {
        id,
      },
      error: true,
    }));
    const jobRes = res.data;
    const {
      groups: { groups },
    } = getState();

    let targetDetail = [];

    for (const t of jobRes.targets || []) {
      if (!t.includes('groups')) {
        const urlSplitted = t.split(defaultConfig.apiUri);
        const url = urlSplitted.length > 1 ? urlSplitted[1] : null;
        if (url) {
          const data = await getJobDependence(url);
          targetDetail.push(data);
        }
      } else {
        targetDetail.push(t);
      }
    }

    targetDetail = targetDetail.filter((t) => !!t);

    const group = getGroupFromJob({ groups, job: jobRes, targetDetail });

    let job = {
      ...jobRes,
      groupDetails: group,
      isJobCancelable: res.error ? true : isJobCancelable(jobRes),
      targetDetail,
    };

    if (jobRes.type === JOB_TYPES.TRANSCODE) {
      const fileUrl = jobRes._embedded[jobRes.targets[0]]?.files[0];
      if (fileUrl) {
        const fileId = getIdFromUrl({ url: fileUrl });
        const dep = await getJobDependence(`${ENDPOINTS.FILES}/${fileId}`);
        job = {
          ...job,
          video: {
            ...dep,
            _embedded: undefined, // No needed
          },
        };
      } else {
        job = {
          ...job,
          video: {
            uri: jobRes.targets[0],
            group: null,
            files: [],
          },
        };
      }
    }

    dispatch(setJob({ job }));
    dispatch(setLoadingJob(LOADING_STATUS.success));
  } catch (error) {
    console.error(error);
    dispatch(setJob({ job: null }));
    dispatch(setLoadingJob(LOADING_STATUS.error));
  }
};

export const initRowExpanded = (id) => async (dispatch, getState) => {
  const {
    adminJobs: { jobs },
  } = getState();
  const jobIndex = jobs.findIndex((j) => j.id === parseInt(id));

  dispatch(
    setExpandedRow({
      expanded: jobIndex !== -1 ? { [jobIndex]: true } : {},
      tableKey: TABLE_KEYS.adminJobs,
    }),
  );
};

export const cancelJob = (job) => async (dispatch, getState) => {
  try {
    const {
      adminJobs: { jobs },
    } = getState();

    const url = `${ENDPOINTS.JOBS}/${job.id}`;

    const body = {
      status: STATUS.canceled,
      description: 'Manually canceled from browser',
      arguments: job.arguments,
      created: job.created,
      id: job.id,
      steps_complete: job.steps_complete,
      steps_total: job.steps_total,
      targets: job.targets,
      type: job.type,
      uri: job.uri,
    };

    const res = await apiService.patch(url, body);
    dispatch(
      setAlert({
        title: 'Job canceled!',
        content: `Job ID ${job.id} has been canceled.`,
        type: 'info',
        show: true,
      }),
    );

    const updatedJobs = jobs.map((j) => (j.id === job.id ? res.data : j));
    if (job.id) {
      const jobIndex = updatedJobs.findIndex(
        (job) => job.id === parseInt(job.id),
      );
      dispatch(
        setExpandedRow({
          expanded: { [jobIndex]: true },
          tableKey: TABLE_KEYS.adminJobs,
        }),
      );
    }
    dispatch(setJobs({ jobs: updatedJobs }));
    dispatch(
      setJob({
        job: {
          ...job,
          ...res.data,
          isJobCancelable: isJobCancelable(res.data),
        },
      }),
    );
  } catch (errors) {
    console.error(errors);
    dispatch(
      setFormErrors({
        id: FORM_ERRORS_IDS.adminJobDetails,
        errors,
      }),
    );
  }
};

export const resubmitJob = (job) => async (dispatch) => {
  try {
    const url = ENDPOINTS.JOBS;
    const body = {
      arguments: job.arguments,
      description: `Resubmitted job. Original job: ${job.uri || ''}`,
      targets: job.targets,
      type: job.type,
    };
    await apiService.post(url, body);
    dispatch(
      setAlert({
        title: 'Job resubmitted!',
        content: `Job ID ${job.id} has been resubmitted.`,
        type: 'info',
        show: true,
      }),
    );
    // Refresh Jobs List
    dispatch(getJobs());
    return true;
  } catch (errors) {
    console.error(errors);
    dispatch(
      setFormErrors({
        id: FORM_ERRORS_IDS.adminJobDetails,
        errors,
      }),
    );
    return Promise.reject(errors);
  }
};
